import {
  Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import type { TrendPoint } from '../../../api/siteAnalyticsApi';

/**
 * The three numbers over time, on one axis.
 *
 * Users, visits and events are deliberately one chart rather than three: the
 * question the page exists to answer is how they move APART — 578 visits and 26
 * users on one simulation is the whole story of that row, and two charts side
 * by side make it arithmetic instead of a picture.
 *
 * Shared Y axis for the same reason, even though events dwarf users on a busy
 * day. Normalising each line to its own scale would draw three lines that rise
 * together and say nothing.
 */
export function Trend({ points, unit }: { points: TrendPoint[]; unit: 'hour' | 'day' }) {
  if (points.length === 0) return <p className="sa-empty">Nothing recorded in this window.</p>;

  /** The bucket as a person reads it: a date by day, a clock time by hour.
   *
   *  Buckets are UTC — a prefix of the event's ISO timestamp. An hour is shown
   *  on the reader's own clock, the one the From / To pickers speak, so the
   *  axis and the window agree; in India a bucket reads 13:30, not 08:00. A
   *  day bucket is a UTC day and is named as one — on a local clock it would
   *  straddle two dates and be named for the wrong one west of Greenwich. */
  const pad = (n: number) => String(n).padStart(2, '0');
  const label = (bucket: string) => {
    if (unit === 'hour') {
      const d = new Date(`${bucket}:00:00Z`);
      return `${d.getDate()} ${d.toLocaleString(undefined, { month: 'short' })} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    return new Date(`${bucket}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };

  // One bucket cannot be a line. Say the numbers instead of drawing a dot
  // nobody can read a trend from.
  if (points.length === 1) {
    const p = points[0];
    return (
      <p className="sa-empty">
        {label(p.bucket)} — {p.users.toLocaleString()} users · {p.visits.toLocaleString()} visits
        · {p.events.toLocaleString()} events, the only {unit} in range.
      </p>
    );
  }

  const data = points.map(p => ({ ...p, label: label(p.bucket) }));

  return (
    <div className="sa-trend">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
          <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" vertical={false} />
          {/* A 90-day window has more dates than fit. `minTickGap` drops the
              labels that would collide, keeping the axis readable at any width
              while the ends stay anchored. */}
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
            stroke="var(--border-color)"
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
            stroke="var(--border-color)"
            allowDecimals={false}
            width={48}
            // Compact, so a four-digit tick fits the axis: at 48px "1,000"
            // was clipped to "000" and read as zero.
            tickFormatter={(v: number) => new Intl.NumberFormat(undefined, { notation: 'compact' }).format(v)}
          />
          <Tooltip
            contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8 }}
            labelStyle={{ color: 'var(--text-primary)' }}
            formatter={(v, name) => [Number(v ?? 0).toLocaleString(), name]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="users" name="Users" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          <Line type="monotone" dataKey="visits" name="Sessions" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          <Line type="monotone" dataKey="events" name="Events" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
